import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { GoogleMapsProvider } from './context/GoogleMapsContext';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { MapPage } from './pages/MapPage';
import { JobsPage } from './pages/JobsPage';
import { UsersPage } from './pages/UsersPage';
import { CustomersPage } from './pages/CustomersPage';
import { ReportsPage } from './pages/ReportsPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { BillingPage } from './pages/BillingPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { SupportPage } from './pages/SupportPage';
import { CompanySettingsPage } from './pages/CompanySettingsPage';
import { BranchesListPage } from './pages/Settings/BranchesListPage';
import { BranchFormPage } from './pages/Settings/BranchFormPage';
import { BranchDetailPage } from './pages/Settings/BranchDetailPage';
import { WorkingHoursPage } from './pages/Settings/WorkingHoursPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PaymentFailPage } from './pages/PaymentFailPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { LegalDocsAdminPage } from './pages/admin/LegalDocsAdminPage';
import { CustomQuotesAdminPage } from './pages/admin/CustomQuotesAdminPage';
import { PaymentSettingsAdminPage } from './pages/admin/PaymentSettingsAdminPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  return user ? children : <Navigate to="/login" replace />;
}

function SuperAdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'superadmin') return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!['admin', 'supervisor', 'superadmin'].includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* PayTR redirect target'ları — tam ekran, AppLayout dışında */}
        <Route path="/odeme/basarili" element={<PaymentSuccessPage />} />
        <Route path="/odeme/basarisiz" element={<PaymentFailPage />} />
        <Route path="/ops-center-f9x2" element={<RequireAuth><SuperAdminOnly><SuperAdminPage /></SuperAdminOnly></RequireAuth>} />
        <Route path="/ops-center-f9x2/legal" element={<RequireAuth><SuperAdminOnly><LegalDocsAdminPage /></SuperAdminOnly></RequireAuth>} />
        <Route path="/ops-center-f9x2/quotes" element={<RequireAuth><SuperAdminOnly><CustomQuotesAdminPage /></SuperAdminOnly></RequireAuth>} />
        <Route path="/ops-center-f9x2/payment-settings" element={<RequireAuth><SuperAdminOnly><PaymentSettingsAdminPage /></SuperAdminOnly></RequireAuth>} />
        <Route
          element={
            <RequireAuth>
              <SocketProvider>
                <AppLayout />
              </SocketProvider>
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="customers" element={<AdminOnly><CustomersPage /></AdminOnly>} />
          <Route path="vehicles" element={<AdminOnly><VehiclesPage /></AdminOnly>} />
          <Route path="reports" element={<AdminOnly><ReportsPage /></AdminOnly>} />
          <Route path="users" element={<AdminOnly><UsersPage /></AdminOnly>} />
          <Route path="billing" element={<AdminOnly><BillingPage /></AdminOnly>} />
          <Route path="faturalar" element={<AdminOnly><InvoicesPage /></AdminOnly>} />
          <Route path="settings" element={<AdminOnly><CompanySettingsPage /></AdminOnly>} />
          <Route path="settings/branches" element={<AdminOnly><BranchesListPage /></AdminOnly>} />
          <Route path="settings/branches/new" element={<AdminOnly><BranchFormPage /></AdminOnly>} />
          <Route path="settings/branches/:id" element={<AdminOnly><BranchDetailPage /></AdminOnly>} />
          <Route path="settings/branches/:id/edit" element={<AdminOnly><BranchFormPage /></AdminOnly>} />
          <Route path="settings/working-hours" element={<AdminOnly><WorkingHoursPage /></AdminOnly>} />
          <Route path="support" element={<SupportPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <GoogleMapsProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </GoogleMapsProvider>
  );
}
